import { Injectable, signal, inject } from '@angular/core';
import { Subject } from 'rxjs';
import {
  RecordingState,
  PermissionStatus,
  RecordingResult
} from '../models/recording-state.model';
import { TRANSCRIBE_ERROR_HANDLER, TranscribeErrorHandler } from './error-handler.token';

/**
 * Recording service that handles:
 * - Microphone permission
 * - Audio recording with pause/resume (WebM/Opus format)
 * - Real-time waveform visualization
 * - Audio chunk streaming for transcription (250ms intervals)
 * - 5-minute maximum recording duration
 */
@Injectable()
export class RecordingService {
  private errorHandler: TranscribeErrorHandler = inject(TRANSCRIBE_ERROR_HANDLER, { optional: true })
    ?? { error: (msg, err) => console.error(`[Transcribe] ${msg}`, err) };

  // Observable state using signals
  readonly state$ = signal<RecordingState>('idle');
  readonly duration$ = signal<number>(0);
  readonly waveform$ = signal<number[]>(new Array(100).fill(0));
  readonly permissionStatus$ = signal<PermissionStatus>('pending');

  // Audio chunk streaming
  private readonly audioChunkSubject = new Subject<Blob>();
  /** Emits audio chunks as they are captured (every 250ms) */
  readonly audioChunk$ = this.audioChunkSubject.asObservable();

  // Private recording state
  private audioChunks: Blob[] = [];
  private mediaRecorder?: MediaRecorder;
  private mediaStream?: MediaStream;

  // Waveform analysis
  private audioContext?: AudioContext;
  private analyserNode?: AnalyserNode;
  private animationFrame?: number;
  private audioSource?: MediaStreamAudioSourceNode;

  // Duration tracking
  private startTime?: number;
  private pausedDuration = 0;
  private pauseStartTime?: number;
  private durationInterval?: ReturnType<typeof setInterval>;
  private readonly chunkDuration = 250; // ms - Shorter chunks for better real-time transcription
  private maxDurationTimer?: ReturnType<typeof setTimeout>;
  private readonly MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutes max recording

  /**
   * Request microphone permission
   */
  async requestPermission(): Promise<PermissionStatus> {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.permissionStatus$.set('denied');
        return 'denied';
      }

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      });

      this.permissionStatus$.set('granted');
      return 'granted';

    } catch (error: unknown) {
      this.errorHandler.warn?.('Microphone permission denied');
      this.permissionStatus$.set('denied');
      return 'denied';
    }
  }

  /**
   * Start recording
   */
  async startRecording(): Promise<void> {
    if (this.state$() !== 'idle') {
      throw new Error('Recording already in progress');
    }

    if (this.permissionStatus$() !== 'granted') {
      const status = await this.requestPermission();
      if (status !== 'granted') {
        throw new Error('Microphone permission required');
      }
    }

    try {
      // Reset state
      this.audioChunks = [];
      this.duration$.set(0);
      this.pausedDuration = 0;

      // Initialize waveform with zeros
      this.waveform$.set(new Array(100).fill(0));

      // Get fresh media stream if needed
      if (!this.mediaStream || !this.mediaStream.active) {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 44100
          }
        });
      }

      // Setup MediaRecorder
      this.setupMediaRecorder(this.mediaStream);

      // Setup waveform analysis
      await this.setupWaveformAnalysis(this.mediaStream);

      // Start recording
      this.mediaRecorder!.start(this.chunkDuration);
      this.startTime = Date.now();
      this.state$.set('recording');

      // Start duration tracking
      this.startDurationTracking();

      // Start waveform animation now that state is 'recording'
      this.updateWaveform();

      // Auto-stop after max duration (5 minutes)
      this.maxDurationTimer = setTimeout(() => {
        this.stopRecording().catch(error => {
          this.errorHandler.error('Failed to auto-stop recording', error);
        });
      }, this.MAX_DURATION_MS);

    } catch (error: unknown) {
      this.errorHandler.error('Failed to start recording', error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Pause recording
   */
  pauseRecording(): void {
    if (this.state$() !== 'recording') {
      return;
    }

    try {
      this.mediaRecorder?.pause();
      this.pauseStartTime = Date.now();
      this.state$.set('paused');

      // Stop waveform updates
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = undefined;
      }

    } catch (error: unknown) {
      this.errorHandler.error('Failed to pause recording', error);
    }
  }

  /**
   * Resume recording
   */
  resumeRecording(): void {
    if (this.state$() !== 'paused') {
      return;
    }

    try {
      this.mediaRecorder?.resume();

      // Track paused duration
      if (this.pauseStartTime) {
        this.pausedDuration += Date.now() - this.pauseStartTime;
        this.pauseStartTime = undefined;
      }

      this.state$.set('recording');

      // Resume waveform updates
      this.updateWaveform();

    } catch (error: unknown) {
      this.errorHandler.error('Failed to resume recording', error);
    }
  }

  /**
   * Stop recording and return result
   */
  async stopRecording(): Promise<RecordingResult> {
    if (this.state$() === 'idle' || this.state$() === 'stopped') {
      throw new Error('No recording in progress');
    }

    return new Promise((resolve, reject) => {
      try {
        // Setup stop handler
        this.mediaRecorder!.onstop = async () => {
          try {
            // Stop duration tracking
            this.stopDurationTracking();

            // Combine all chunks into final audio blob
            const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm;codecs=opus' });

            // Get final waveform
            const waveform = this.waveform$();

            // Get final duration
            const duration = this.duration$();

            // Cleanup
            this.cleanup();

            this.state$.set('stopped');

            resolve({
              audioBlob,
              duration,
              waveform
            });

          } catch (error: unknown) {
            this.errorHandler.error('Error processing recording result', error);
            reject(error);
          }
        };

        // Stop the recorder
        this.mediaRecorder!.stop();

      } catch (error: unknown) {
        this.errorHandler.error('Failed to stop recording', error);
        reject(error);
      }
    });
  }

  /**
   * Check if recording
   */
  isRecording(): boolean {
    return this.state$() === 'recording';
  }

  /**
   * Check if paused
   */
  isPaused(): boolean {
    return this.state$() === 'paused';
  }

  /**
   * Check if can record
   */
  canRecord(): boolean {
    return this.permissionStatus$() === 'granted' && this.state$() === 'idle';
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.cleanup();
    this.audioChunkSubject.complete();
  }

  // ==================== Private Methods ====================

  /**
   * Setup MediaRecorder
   */
  private setupMediaRecorder(stream: MediaStream): void {
    try {
      // Use WebM-Opus format (widely supported across browsers, works with Deepgram)
      const options = { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 128000 };

      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        this.mediaRecorder = new MediaRecorder(stream, options);
      } else {
        // Fallback to default
        this.mediaRecorder = new MediaRecorder(stream);
      }

      // Handle data available
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.audioChunks.push(event.data);
          // Emit chunk for Deepgram streaming
          this.audioChunkSubject.next(event.data);
        }
      };

      // Handle errors
      this.mediaRecorder.onerror = (event: Event) => {
        this.errorHandler.error('MediaRecorder error', event);
      };

    } catch (error: unknown) {
      this.errorHandler.error('Failed to setup MediaRecorder', error);
      throw error;
    }
  }

  /**
   * Setup waveform analysis
   */
  private async setupWaveformAnalysis(stream: MediaStream): Promise<void> {
    try {
      // Create audio context with explicit sample rate for mobile compatibility
      this.audioContext = new AudioContext({ sampleRate: 44100 });

      // Resume audio context (required by browsers after user interaction)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create analyser node
      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      this.analyserNode.smoothingTimeConstant = 0.8;

      // Connect stream to analyser
      this.audioSource = this.audioContext.createMediaStreamSource(stream);
      this.audioSource.connect(this.analyserNode);

    } catch (error: unknown) {
      this.errorHandler.error('Failed to setup waveform analysis', error);
      // Non-fatal, continue without waveform
    }
  }

  /**
   * Update waveform visualization
   */
  private updateWaveform(): void {
    if (!this.analyserNode || this.state$() !== 'recording') {
      return;
    }

    // Check AudioContext state on mobile
    if (this.audioContext?.state !== 'running') {
      this.audioContext?.resume();
    }

    const bufferLength = this.analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    this.analyserNode.getByteTimeDomainData(dataArray);

    // Downsample to 100 points for visualization
    const targetPoints = 100;
    const blockSize = Math.floor(bufferLength / targetPoints);
    const waveform: number[] = [];

    for (let i = 0; i < targetPoints; i++) {
      const start = i * blockSize;
      const end = Math.min(start + blockSize, bufferLength);

      // Calculate average amplitude for this block
      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += Math.abs(dataArray[j] - 128); // Normalize around 128
      }

      const avg = sum / (end - start);
      // Amplify for better visualization (multiply by 2.5)
      const normalized = Math.min(1, (avg / 128) * 2.5);
      waveform.push(normalized);
    }

    this.waveform$.set(waveform);

    // Continue animation
    this.animationFrame = requestAnimationFrame(() => this.updateWaveform());
  }

  /**
   * Start duration tracking
   */
  private startDurationTracking(): void {
    this.durationInterval = setInterval(() => {
      if (this.startTime && this.state$() === 'recording') {
        const elapsed = Date.now() - this.startTime - this.pausedDuration;
        this.duration$.set(elapsed);
      }
    }, 100); // Update every 100ms
  }

  /**
   * Stop duration tracking
   */
  private stopDurationTracking(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = undefined;
    }

    // Clear max duration timer
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
      this.maxDurationTimer = undefined;
    }
  }

  /**
   * Cleanup all resources
   */
  private cleanup(): void {
    // Stop animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }

    // Stop duration tracking
    this.stopDurationTracking();

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = undefined;
    }

    this.analyserNode = undefined;
    this.audioSource = undefined;

    // Stop media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = undefined;
    }

    this.mediaRecorder = undefined;

    // Reset state (keep audioChunks for playback)
    this.startTime = undefined;
    this.pausedDuration = 0;
    this.pauseStartTime = undefined;
  }
}
